import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Brain, RefreshCw, Sparkles, ArrowRight, Check, Pencil, X as XIcon,
  TrendingUp, TrendingDown, Minus, ChevronRight, FileText, Building2,
} from "lucide-react";
import { toast } from "sonner";
import { Layout } from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from "../components/ui/accordion";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import { useApp } from "../context/AppContext";
import { useLang } from "../lib/i18n";
import { api, formatError } from "../lib/api";
import { Skeleton } from "../components/ui/skeleton";
import { NextStepCta } from "../components/NextStepCta";

const LEVEL_STYLE = {
  critique: "bg-red-500/15 text-red-300 border-red-500/30",
  eleve: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  surveiller: "bg-sky-500/15 text-sky-300 border-sky-500/30",
  opportunite: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
};
const LEVEL_ACCENT = { critique: "#EF4444", eleve: "#F59E0B", surveiller: "#38BDF8", opportunite: "#22C55E" };
const scrollToPriority = (i) => {
  const el = document.querySelector(`[data-testid="priority-${i}"]`);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
};
const STATUS_STYLE = {
  todo: "bg-white/5 text-[hsl(var(--muted-foreground))] border-white/10",
  progress: "bg-sky-500/15 text-sky-300 border-sky-500/30",
  done: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  reevaluate: "bg-amber-500/15 text-amber-300 border-amber-500/30",
};
const STATUS_ORDER = ["todo", "progress", "done", "reevaluate"];
const MODULE_ROUTE = {
  content: "/app/content", calendar: "/app/calendar", reputation: "/app/reputation",
  loyalty: "/app/loyalty", growth: "/app/growth", command: "/app/command",
};
const EXEC_CTA_KEY = {
  content: "exec_cta_content", calendar: "exec_cta_calendar", reputation: "exec_cta_reputation",
  loyalty: "exec_cta_loyalty", growth: "exec_cta_growth", command: "exec_cta_command",
};
const SRC_KEY = { extrait: "diag_observed", saisi: "diag_declared", calcul: "diag_calc" };

function fmtNum(v, unit, lang) {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  const s = n.toLocaleString(lang === "en" ? "en-US" : lang === "es" ? "es-ES" : "fr-FR",
    { maximumFractionDigits: Math.abs(n % 1) > 1e-9 ? 1 : 0 });
  return unit ? `${s}\u00a0${unit}` : s;
}

function ProvBadge({ source, t }) {
  if (!source) return null;
  const key = SRC_KEY[source];
  if (!key) return null;
  return <span data-testid={`prov-${source}`} className="text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded-md bg-white/5 border border-white/10 text-[hsl(var(--muted-foreground))]">{t(key)}</span>;
}

function FactCard({ f, t, lang }) {
  return (
    <div className="rounded-xl bg-white/[0.03] border border-white/5 p-3" data-testid={`kpi-${f.key}`}>
      <div className="flex items-start justify-between gap-2 mb-1">
        <p className="text-[11px] uppercase text-[hsl(var(--muted-foreground))] leading-tight">{f.label}</p>
      </div>
      <p className="text-lg font-display font-black">{fmtNum(f.value, f.unit, lang)}</p>
      <div className="mt-1"><ProvBadge source={f.source} t={t} /></div>
    </div>
  );
}

function SynthBlock({ title, items, tone, t }) {
  if (!items || !items.length) return null;
  return (
    <AccordionItem value={title} className="border-white/5">
      <AccordionTrigger className="text-sm font-semibold hover:no-underline">
        <span className={tone}>{title}</span>
        <span className="ml-2 text-xs text-[hsl(var(--muted-foreground))]">({items.length})</span>
      </AccordionTrigger>
      <AccordionContent>
        <div className="space-y-3">
          {items.map((it, i) => (
            <div key={i} className="rounded-xl bg-white/[0.02] border border-white/5 p-3">
              <p className="font-semibold text-sm mb-1.5">{it.titre}</p>
              {it.fait && <p className="text-xs mb-1"><span className="text-neon font-semibold">{t("diag_fact")} : </span>{it.fait}</p>}
              {it.interpretation && <p className="text-xs text-[hsl(var(--muted-foreground))]"><span className="font-semibold">{t("diag_interp")} : </span>{it.interpretation}</p>}
            </div>
          ))}
        </div>
      </AccordionContent>
    </AccordionItem>
  );
}

const OBS_CATS = ["concurrent", "travaux", "meteo", "absence", "fournisseur", "event", "prix", "autre"];

function ObservationsCard({ t, lang }) {
  const [obs, setObs] = useState([]);
  const [open, setOpen] = useState(false);
  const [cat, setCat] = useState("concurrent");
  const [date, setDate] = useState("");
  const [comment, setComment] = useState("");
  useEffect(() => { api.get("/observations").then(({ data }) => setObs(data.observations || [])).catch(() => {}); }, []);
  const persist = async (next) => {
    setObs(next);
    try { await api.put("/observations", { observations: next }); } catch { toast.error("Erreur d'enregistrement."); }
  };
  const add = () => {
    if (!comment.trim()) { toast.error(t("obs_comment_ph")); return; }
    persist([...obs, { category: cat, date: date.trim(), comment: comment.trim(), at: new Date().toISOString() }]);
    setComment(""); setDate(""); toast.success(t("obs_saved"));
  };
  const remove = (i) => persist(obs.filter((_, j) => j !== i));
  return (
    <section className="mb-6" data-testid="obs-card">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between lb-card p-3 text-left" data-testid="obs-toggle">
        <span><span className="font-display font-bold text-base">{t("obs_title")}</span> <span className="text-xs text-[hsl(var(--muted-foreground))] ml-1">{obs.length > 0 ? `(${obs.length})` : ""}</span></span>
        <span className="text-neon text-sm">{open ? "–" : "+"}</span>
      </button>
      {open && (
        <div className="lb-card p-4 mt-2 space-y-3">
          <p className="text-xs text-[hsl(var(--muted-foreground))]">{t("obs_rule")}</p>
          {obs.length > 0 && (
            <div className="space-y-1.5">
              {obs.map((o, i) => (
                <div key={i} className="flex items-center gap-2 text-xs p-2 rounded-lg bg-white/[0.03] border border-white/5" data-testid={`obs-item-${i}`}>
                  <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-neon/10 text-neon border border-neon/20">{t(`obs_cat_${o.category}`) || o.category}</span>
                  {o.date && <span className="text-[hsl(var(--muted-foreground))]">{o.date}</span>}
                  <span className="flex-1">{o.comment}</span>
                  <button onClick={() => remove(i)} className="text-[hsl(var(--muted-foreground))] hover:text-red-400" data-testid={`obs-del-${i}`}>✕</button>
                </div>
              ))}
            </div>
          )}
          <div className="grid sm:grid-cols-[auto_auto_1fr_auto] gap-2 items-center">
            <select value={cat} onChange={(e) => setCat(e.target.value)} data-testid="obs-add-category" className="h-10 rounded-lg bg-white/5 border border-white/10 text-sm px-2">
              {OBS_CATS.map((c) => <option key={c} value={c}>{t(`obs_cat_${c}`)}</option>)}
            </select>
            <Input value={date} onChange={(e) => setDate(e.target.value)} placeholder={t("obs_date_ph")} data-testid="obs-add-date" className="h-10 bg-white/5 border-white/10 w-full sm:w-28" />
            <Input value={comment} onChange={(e) => setComment(e.target.value)} placeholder={t("obs_comment_ph")} data-testid="obs-add-comment" className="h-10 bg-white/5 border-white/10" />
            <Button onClick={add} data-testid="obs-add-btn" className="h-10 rounded-full bg-gradient-to-r from-primary to-neon text-white lb-glow-btn text-sm px-4">{t("obs_add")}</Button>
          </div>
        </div>
      )}
    </section>
  );
}

export default function Diagnostic360() {
  const nav = useNavigate();
  const { demo, setPendingContent, setPendingCommand } = useApp();
  const { t, lang } = useLang();
  const [diag, setDiag] = useState(null);
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [editObj, setEditObj] = useState(null);
  const [editVal, setEditVal] = useState("");
  const [chooser, setChooser] = useState(false);
  const [availDocs, setAvailDocs] = useState([]);
  const [srcMode, setSrcMode] = useState(() => (typeof localStorage !== "undefined" && localStorage.getItem("lb_diag_src_mode")) || "full");
  const [selDocs, setSelDocs] = useState([]);

  useEffect(() => { if (demo) nav("/app/dashboard"); }, [demo, nav]);

  useEffect(() => {
    (async () => {
      try {
        const [d, p] = await Promise.all([api.get("/diagnostic"), api.get("/action-plan")]);
        if (d.data && d.data.diagnostic_id) setDiag(d.data);
        if (p.data && p.data.plan_id) setPlan(p.data);
      } catch { /* vide */ }
      setLoading(false);
    })();
  }, []);

  const location = useLocation();
  useEffect(() => {
    const did = location.state && location.state.enrichDoc;
    if (!did) return;
    (async () => {
      try {
        const { data } = await api.get("/documents");
        const docs = Array.isArray(data) ? data : [];
        setAvailDocs(docs);
        setSelDocs(docs.filter((d) => d.document_id === did).map((d) => d.document_id));
        setSrcMode("full");
        setChooser(true);
      } catch { /* ignore */ }
    })();
  }, [location.state]);

  const ctxBadge = useMemo(() => {
    const c = diag && diag.analysis_context;
    if (!c || !c.mode) return null;
    const n = (c.document_ids || []).length;
    if (c.mode === "profile") return t("diag_ctx_profile");
    if (c.mode === "documents") return `${n} ${t("diag_ctx_docs_suf")}`;
    return `${t("diag_ctx_full_pre")} ${n} ${t("diag_ctx_docs_suf")}`;
  }, [diag, t]);
  const runGenerate = async (ctx) => {
    setBusy(true);
    const before = diag?.created_at || null;
    try {
      const { data } = await api.post("/diagnostic/generate", ctx ? { analysis_context: ctx } : {}, { timeout: 180000 });
      setDiag(data);
      if (ctx?.mode && typeof localStorage !== "undefined") localStorage.setItem("lb_diag_src_mode", ctx.mode);
      if (data.cached) toast.success(t("diag_cached"));
      setBusy(false);
      return;
    } catch (e) {
      // Génération lente : le backend continue de générer. On récupère le résultat par POLLING
      // (uniquement des GET, aucune 2e génération). Le bouton reste désactivé pendant ce temps.
      toast(t("diag_wait"));
      const ok = await pollDiagnostic(before);
      if (!ok) toast.error(formatError(e?.response?.data?.detail));
      setBusy(false);
    }
  };

  const pollDiagnostic = async (before) => {
    for (let i = 0; i < 30; i++) {
      await new Promise((r) => setTimeout(r, 5000));
      try {
        const { data } = await api.get("/diagnostic");
        if (data && data.diagnostic_id && data.created_at !== before) { setDiag(data); return true; }
      } catch { /* on continue d'attendre */ }
    }
    return false;
  };

  // Entrée : détermine les sources disponibles. Si Profil ET Documents existent, on demande
  // un choix EXPLICITE (jamais de "Vue complète" silencieuse). Sinon on utilise la seule
  // source disponible (autorisée de fait), sans imposer de choix inutile.
  const generate = async () => {
    let completion = 0, docs = [];
    try {
      const [p, d] = await Promise.all([api.get("/company-profile"), api.get("/documents")]);
      completion = p.data?.completion ?? 0;
      docs = Array.isArray(d.data) ? d.data : [];
    } catch { /* réseau : on tente le comportement par défaut */ }
    setAvailDocs(docs);
    const hasProfile = completion > 0;
    const hasDocs = docs.length > 0;
    if (hasProfile && hasDocs) {
      const last = (typeof localStorage !== "undefined" && localStorage.getItem("lb_diag_src_mode")) || "full";
      setSrcMode(last);
      setSelDocs(docs.map((x) => x.document_id));
      setChooser(true);
      return;
    }
    if (hasDocs) return runGenerate({ mode: "documents", document_ids: docs.map((x) => x.document_id) });
    if (hasProfile) return runGenerate({ mode: "profile", document_ids: [] });
    return runGenerate(null);
  };

  const confirmChooser = async () => {
    if (srcMode !== "profile" && selDocs.length === 0) { toast.error(t("diag_src_need_doc")); return; }
    const ctx = srcMode === "profile"
      ? { mode: "profile", document_ids: [] }
      : srcMode === "documents"
      ? { mode: "documents", document_ids: selDocs }
      : { mode: "full", document_ids: selDocs };
    setChooser(false);
    await runGenerate(ctx);
  };

  const toggleSel = (id) => setSelDocs((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  const buildPlan = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/action-plan/generate", {});
      setPlan(data);
      toast.success(t("diag_plan"));
    } catch (e) { toast.error(formatError(e?.response?.data?.detail)); }
    setBusy(false);
  };

  const cycleStatus = async (action) => {
    const idx = STATUS_ORDER.indexOf(action.status);
    const nextStatus = STATUS_ORDER[(idx + 1) % STATUS_ORDER.length];
    try {
      const { data } = await api.put(`/action-plan/action/${action.id}`, { status: nextStatus });
      setPlan((p) => ({ ...p, horizons: data.horizons }));
    } catch (e) { toast.error(formatError(e?.response?.data?.detail)); }
  };

  const updateObjective = async (obj, status, cible) => {
    try {
      const { data } = await api.put(`/action-plan/objective/${obj.id}`, { status, ...(cible !== undefined ? { cible } : {}) });
      setPlan((p) => ({ ...p, objectives: data.objectives }));
    } catch (e) { toast.error(formatError(e?.response?.data?.detail)); }
  };

  const openInModule = (action) => {
    const route = MODULE_ROUTE[action.module];
    if (!route) return;
    if (action.module === "content") setPendingContent({ topic: action.title, why: action.reason || "" });
    if (action.module === "command") setPendingCommand(action.title);
    nav(route);
  };

  const allFacts = useMemo(() => {
    if (!diag) return [];
    return [...(diag.facts || []), ...(diag.evolutions || [])];
  }, [diag]);

  const sourceChooser = (
    <Dialog open={chooser} onOpenChange={setChooser}>
      <DialogContent className="glass border-white/10 w-[calc(100%-2rem)] max-w-md rounded-2xl max-h-[85vh] overflow-y-auto" data-testid="diag-source-chooser">
        <DialogHeader><DialogTitle className="text-base pr-6">{t("diag_src_title")}</DialogTitle></DialogHeader>
        <p className="text-xs text-[hsl(var(--muted-foreground))] -mt-1 mb-2">{t("diag_src_sub")}</p>
        <div className="space-y-2">
          {[
            { m: "profile", icon: Building2, label: t("diag_src_profile"), desc: t("diag_src_profile_desc") },
            { m: "documents", icon: FileText, label: t("diag_src_documents"), desc: t("diag_src_documents_desc") },
            { m: "full", icon: Sparkles, label: t("diag_src_full"), desc: t("diag_src_full_desc") },
          ].map(({ m, icon: Icon, label, desc }) => (
            <button key={m} type="button" onClick={() => setSrcMode(m)} data-testid={`src-mode-${m}`}
              className={`w-full text-left p-3 rounded-xl border flex items-start gap-3 transition-colors ${srcMode === m ? "border-neon/60 bg-neon/[0.06]" : "border-white/10 bg-white/[0.02] hover:bg-white/5"}`}>
              <Icon size={18} className={`shrink-0 mt-0.5 ${srcMode === m ? "text-neon" : "text-[hsl(var(--muted-foreground))]"}`} />
              <div className="min-w-0"><p className="text-sm font-semibold">{label}</p><p className="text-xs text-[hsl(var(--muted-foreground))]">{desc}</p></div>
            </button>
          ))}
        </div>
        {srcMode !== "profile" && (
          <div className="mt-3">
            <p className="text-xs font-semibold mb-2">{t("diag_src_choose_docs")}</p>
            {availDocs.length === 0 ? (
              <p className="text-xs text-[hsl(var(--muted-foreground))]">{t("diag_src_no_docs")}</p>
            ) : (
              <div className="space-y-1.5 max-h-40 overflow-y-auto pr-1">
                {availDocs.map((d) => (
                  <label key={d.document_id} data-testid={`src-doc-${d.document_id}`}
                    className="flex items-center gap-2.5 p-2 rounded-lg bg-white/[0.02] border border-white/5 cursor-pointer">
                    <Checkbox checked={selDocs.includes(d.document_id)} onCheckedChange={() => toggleSel(d.document_id)} />
                    <span className="text-xs truncate flex-1">{d.filename}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        )}
        <div className="flex gap-2 mt-4">
          <Button variant="outline" onClick={() => setChooser(false)} className="flex-1 rounded-full h-11 border-white/10 bg-white/5" data-testid="src-cancel">{t("diag_src_cancel")}</Button>
          <Button onClick={confirmChooser} disabled={busy} className="flex-1 rounded-full h-11 bg-gradient-to-r from-primary to-neon text-white font-semibold" data-testid="src-confirm">
            {busy ? <RefreshCw size={15} className="animate-spin" /> : t("diag_src_confirm")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );

  if (loading) return (
    <Layout>
      <div className="max-w-4xl mx-auto pb-10" data-testid="diagnostic-loading">
        <div className="flex items-center gap-2 mb-5"><Skeleton className="h-6 w-6 rounded-lg" /><Skeleton className="h-7 w-56" /></div>
        <Skeleton className="h-20 w-full rounded-2xl mb-5" />
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2.5 mb-6">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-20 rounded-xl" />)}
        </div>
        <Skeleton className="h-6 w-40 mb-3" />
        <div className="space-y-3">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-24 w-full rounded-2xl" />)}</div>
      </div>
    </Layout>
  );

  // -------- Empty state --------
  if (!diag) {
    return (
      <Layout>
        <div className="max-w-xl mx-auto text-center py-14" data-testid="diagnostic-empty">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-primary to-neon flex items-center justify-center mb-5"><Brain size={30} className="text-white" /></div>
          <h1 className="font-display font-black text-2xl mb-2">{t("diag_empty_title")}</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mb-6">{t("diag_empty_text")}</p>
          <Button onClick={generate} disabled={busy} data-testid="diag-generate-btn" className="rounded-full h-12 px-6 bg-gradient-to-r from-primary to-neon text-white lb-glow-btn font-semibold">
            {busy ? <><RefreshCw size={16} className="mr-2 animate-spin" /> {t("diag_generating")}</> : <><Sparkles size={16} className="mr-2" /> {t("diag_generate")}</>}
          </Button>
        </div>
        {sourceChooser}
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto pb-10" data-testid="diagnostic-page">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-5">
          <div>
            <div className="flex items-center gap-2 mb-1"><Brain size={20} className="text-neon" /><h1 className="font-display font-black text-2xl">{t("diag_title")}</h1></div>
            <p className="text-sm text-[hsl(var(--muted-foreground))]">{t("diag_sub")}</p>
            {ctxBadge && (
              <span data-testid="diag-context-badge" className="inline-flex items-center gap-1 mt-1.5 text-[11px] px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-[hsl(var(--muted-foreground))]">
                <FileText size={11} /> {t("diag_ctx_sources")} : {ctxBadge}
              </span>
            )}
          </div>
          <Button onClick={generate} disabled={busy} variant="outline" className="rounded-full h-10 px-4 border-white/10 bg-white/5 text-sm shrink-0" data-testid="diag-refresh-btn">
            <RefreshCw size={15} className={`mr-1.5 ${busy ? "animate-spin" : ""}`} /> {t("diag_refresh")}
          </Button>
        </div>

        <ObservationsCard t={t} lang={lang} />

        {/* Résumé */}
        {diag.resume && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="lb-card p-4 mb-5 border-neon/20" data-testid="diag-resume">
            <p className="text-sm leading-relaxed">{diag.resume}</p>
          </motion.div>
        )}

        {/* Synthèse visuelle des priorités (lecture seule, reflète priorities existantes — max 3) */}
        {diag.priorities && diag.priorities.length > 0 && (
          <section className="mb-6" data-testid="diag-priorities-summary">
            <h2 className="font-display font-bold text-lg mb-3">{t("diag_summary_title")}</h2>
            <div className="grid gap-3 sm:grid-cols-3">
              {diag.priorities.slice(0, 3).map((p, i) => (
                <button key={i} type="button" onClick={() => scrollToPriority(i)}
                  data-testid={`priority-summary-${i}`}
                  className="text-left lb-card p-4 border-l-2 hover:bg-white/[0.04] active:bg-white/[0.06] transition-colors flex flex-col gap-2"
                  style={{ borderLeftColor: LEVEL_ACCENT[p.niveau] || LEVEL_ACCENT.eleve }}>
                  <div className="flex items-center gap-2">
                    <span className="shrink-0 w-6 h-6 rounded-full bg-white/10 text-xs font-bold flex items-center justify-center">{i + 1}</span>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md border ${LEVEL_STYLE[p.niveau] || LEVEL_STYLE.eleve}`} data-testid={`priority-summary-level-${i}`}>{t(`lvl_${p.niveau}`)}</span>
                  </div>
                  <p className="font-semibold text-sm leading-snug">{p.titre}</p>
                  {p.constat && <p className="text-xs text-[hsl(var(--muted-foreground))] line-clamp-2">{p.constat}</p>}
                  <div className="flex flex-wrap gap-1.5 mt-auto pt-1">
                    {p.type && <span className="text-[10px] px-2 py-0.5 rounded-md bg-white/5 border border-white/10 text-[hsl(var(--muted-foreground))]" data-testid={`priority-summary-type-${i}`}>{t(`diag_type_${p.type}`)}</span>}
                    {p.confiance && <span className="text-[10px] px-2 py-0.5 rounded-md bg-white/5 border border-white/10 text-[hsl(var(--muted-foreground))]" data-testid={`priority-summary-confidence-${i}`}>{t("diag_confidence")} : {t(`conf_${p.confiance}`)}</span>}
                  </div>
                </button>
              ))}
            </div>
          </section>
        )}

        {/* Aperçu KPI */}
        {allFacts.length > 0 && (
          <section className="mb-6" data-testid="diag-overview">
            <h2 className="font-display font-bold text-lg mb-3">{t("diag_overview")}</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2.5">
              {allFacts.map((f) => <FactCard key={f.key} f={f} t={t} lang={lang} />)}
            </div>
          </section>
        )}

        {/* Perception vs réalité */}
        {diag.perception_gaps && diag.perception_gaps.length > 0 && (
          <section className="mb-6" data-testid="diag-perception">
            <h2 className="font-display font-bold text-lg mb-3">{t("diag_perception")}</h2>
            <div className="space-y-2">
              {diag.perception_gaps.map((p, i) => (
                <div key={i} className="lb-card p-3 flex items-center justify-between gap-3" data-testid={`perception-${p.key}`}>
                  <p className="text-sm font-medium">{p.label}</p>
                  <div className="flex items-center gap-3 text-xs">
                    <span className="text-[hsl(var(--muted-foreground))]">{t("diag_declared")} : <b className="text-white">{fmtNum(p.declared, p.unit, lang)}</b></span>
                    <ArrowRight size={12} className="text-neon" />
                    <span className="text-[hsl(var(--muted-foreground))]">{t("diag_observed")} : <b className="text-neon">{fmtNum(p.observed, p.unit, lang)}</b></span>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Synthèse forces/faiblesses/risques/opportunités */}
        {(diag.forces?.length || diag.faiblesses?.length || diag.risques?.length || diag.opportunites?.length) ? (
          <section className="mb-6" data-testid="diag-synthesis">
            <Accordion type="single" collapsible className="lb-card px-4">
              <SynthBlock title={t("diag_forces")} items={diag.forces} tone="text-emerald-300" t={t} />
              <SynthBlock title={t("diag_weak")} items={diag.faiblesses} tone="text-red-300" t={t} />
              <SynthBlock title={t("diag_risks")} items={diag.risques} tone="text-amber-300" t={t} />
              <SynthBlock title={t("diag_opps")} items={diag.opportunites} tone="text-sky-300" t={t} />
            </Accordion>
          </section>
        ) : null}

        {/* Priorités */}
        {diag.priorities && diag.priorities.length > 0 && (
          <section className="mb-6" data-testid="diag-priorities">
            <h2 className="font-display font-bold text-lg mb-3">{t("diag_priorities")}</h2>
            <div className="space-y-3">
              {diag.priorities.map((p, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                  className="lb-card p-4" data-testid={`priority-${i}`}>
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md border ${LEVEL_STYLE[p.niveau] || LEVEL_STYLE.eleve}`} data-testid={`priority-level-${i}`}>{t(`lvl_${p.niveau}`)}</span>
                    {p.type && <span className="text-[10px] px-2 py-0.5 rounded-md bg-white/5 border border-white/10 text-[hsl(var(--muted-foreground))]" data-testid={`priority-type-${i}`}>{t(`diag_type_${p.type}`)}</span>}
                    {p.confiance && <span className="text-[10px] px-2 py-0.5 rounded-md bg-white/5 border border-white/10 text-[hsl(var(--muted-foreground))]" data-testid={`priority-confidence-${i}`}>{t("diag_confidence")} : {t(`conf_${p.confiance}`)}</span>}
                    <span className="text-xs text-[hsl(var(--muted-foreground))]">{p.horizon ? `${p.horizon}j` : ""}</span>
                  </div>
                  <p className="font-semibold mb-1">{p.titre}</p>
                  {p.localisation && <p className="text-xs mb-1"><span className="font-semibold text-neon">{t("diag_localisation")} : </span>{p.localisation}{p.contribution ? ` · ${t("diag_contribution")} : ${p.contribution}` : ""}</p>}
                  {p.constat && <p className="text-sm text-[hsl(var(--muted-foreground))] mb-2">{p.constat}</p>}
                  {p.hypotheses && p.hypotheses.length > 0 && (
                    <div className="mb-2" data-testid={`priority-hypotheses-${i}`}>
                      <p className="text-[10px] uppercase text-[hsl(var(--muted-foreground))] mb-0.5">{p.cause_demontree ? t("diag_hypotheses") : `${t("diag_hypotheses")} · ${t("diag_cause_hint")}`}</p>
                      <ul className="list-disc list-inside text-xs text-[hsl(var(--muted-foreground))]">{p.hypotheses.map((h, j) => <li key={j}>{h}</li>)}</ul>
                    </div>
                  )}
                  {p.controles_terrain && p.controles_terrain.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-2" data-testid={`priority-fieldchecks-${i}`}>
                      <span className="text-[10px] uppercase text-[hsl(var(--muted-foreground))] w-full">{t("diag_field_checks")}</span>
                      {p.controles_terrain.map((c, j) => <span key={j} className="text-[11px] px-2 py-0.5 rounded-md bg-white/5 border border-white/10 text-[hsl(var(--muted-foreground))]">{c}</span>)}
                    </div>
                  )}
                  {p.donnees_manquantes && p.donnees_manquantes.length > 0 && (
                    <div className="mb-2 text-xs" data-testid={`priority-missing-${i}`}>
                      <span className="font-semibold text-amber-300">{t("diag_missing_title")} : </span>
                      <span className="text-[hsl(var(--muted-foreground))]">{p.donnees_manquantes.join(" · ")}</span>
                    </div>
                  )}
                  {p.kpis && p.kpis.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {p.kpis.map((k, j) => <span key={j} className="text-[11px] px-2 py-0.5 rounded-md bg-neon/10 text-neon border border-neon/20">{k}</span>)}
                    </div>
                  )}
                  {p.impact && <p className="text-xs mb-2"><span className="font-semibold text-amber-300">{t("diag_impact")} : </span>{p.impact}</p>}
                  {p.objectif && p.objectif.libelle && (
                    <div className="rounded-xl bg-white/[0.03] border border-white/5 p-2.5 mt-2">
                      <p className="text-[10px] uppercase text-[hsl(var(--muted-foreground))] mb-0.5">{p.objectif.source === "vous" ? t("diag_obj_you") : t("diag_obj_lb")}</p>
                      <p className="text-sm">{p.objectif.libelle}{p.objectif.cible ? ` — ${p.objectif.cible}` : ""}</p>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </section>
        )}

        {/* Données manquantes (déterministe, pack métier) */}
        {diag.analysis && diag.analysis.missing_data && diag.analysis.missing_data.length > 0 && (
          <section className="mb-6" data-testid="diag-missing-data">
            <h2 className="font-display font-bold text-lg mb-3">{t("diag_missing_title")}</h2>
            <div className="lb-card p-4">
              <p className="text-sm text-[hsl(var(--muted-foreground))] mb-2">{t("diag_missing_intro")}</p>
              <ul className="list-disc list-inside space-y-1">
                {diag.analysis.missing_data.map((m, i) => (
                  <li key={i} className="text-sm" data-testid={`missing-${m.need || i}`}>
                    {(m.question && (m.question[lang] || m.question.fr)) || m.need}
                  </li>
                ))}
              </ul>
            </div>
          </section>
        )}

        {/* Évolution depuis le dernier diagnostic */}
        {diag.evolution && diag.evolution.length > 0 && (
          <section className="mb-6" data-testid="diag-evolution">
            <h2 className="font-display font-bold text-lg mb-3">{t("diag_evolution")}</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
              {diag.evolution.map((e) => {
                const Icon = e.trend === "ameliore" ? TrendingUp : e.trend === "degrade" ? TrendingDown : Minus;
                const color = e.trend === "ameliore" ? "text-emerald-400" : e.trend === "degrade" ? "text-red-400" : "text-[hsl(var(--muted-foreground))]";
                return (
                  <div key={e.key} className="rounded-xl bg-white/[0.03] border border-white/5 p-3" data-testid={`evolution-${e.key}`}>
                    <p className="text-[11px] uppercase text-[hsl(var(--muted-foreground))] leading-tight mb-1">{e.label}</p>
                    <div className={`flex items-center gap-1.5 ${color}`}>
                      <Icon size={15} />
                      <span className="text-sm font-semibold">{e.pct >= 0 ? "+" : ""}{fmtNum(e.pct, "%", lang)}</span>
                      <span className="text-[10px]">{t(`trend_${e.trend}`)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Plan d'action 30/60/90 */}
        <section className="mb-4" data-testid="diag-plan-section">
          <div className="flex items-center justify-between gap-2 mb-3">
            <h2 className="font-display font-bold text-lg">{t("diag_plan")}</h2>
            {!plan && (
              <Button onClick={buildPlan} disabled={busy} className="rounded-full h-10 px-4 bg-gradient-to-r from-primary to-neon text-white lb-glow-btn text-sm font-semibold" data-testid="diag-build-plan-btn">
                {busy ? <RefreshCw size={15} className="animate-spin" /> : <>{t("diag_build_plan")} <ArrowRight size={15} className="ml-1.5" /></>}
              </Button>
            )}
          </div>

          {plan && (
            <>
              {/* Objectifs */}
              {plan.objectives && plan.objectives.length > 0 && (
                <div className="lb-card p-4 mb-3" data-testid="plan-objectives">
                  <p className="text-xs uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-2">{t("diag_objective")}</p>
                  <div className="space-y-2">
                    {plan.objectives.map((o) => (
                      <div key={o.id} className="flex items-center justify-between gap-2 flex-wrap rounded-xl bg-white/[0.02] border border-white/5 p-2.5" data-testid={`objective-${o.id}`}>
                        <div className="min-w-0">
                          <p className="text-[10px] uppercase text-[hsl(var(--muted-foreground))]">{o.source === "vous" ? t("diag_obj_you") : t("diag_obj_lb")}</p>
                          <p className="text-sm truncate">{o.libelle}{o.cible ? ` — ${o.cible}` : ""}</p>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          {o.status === "valide" && <span className="text-[10px] px-2 py-0.5 rounded-md bg-emerald-500/15 text-emerald-300 border border-emerald-500/30" data-testid={`obj-status-${o.id}`}>{t("diag_validate")}</span>}
                          {o.status === "modifie" && <span className="text-[10px] px-2 py-0.5 rounded-md bg-sky-500/15 text-sky-300 border border-sky-500/30" data-testid={`obj-status-${o.id}`}>{t("diag_modify")}</span>}
                          {o.status === "refuse" && <span className="text-[10px] px-2 py-0.5 rounded-md bg-red-500/15 text-red-300 border border-red-500/30" data-testid={`obj-status-${o.id}`}>{t("diag_refuse")}</span>}
                          <button onClick={() => updateObjective(o, "valide")} title={t("diag_validate")} className="inline-flex items-center justify-center min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 p-1.5 rounded-lg hover:bg-emerald-500/15 text-emerald-300" data-testid={`obj-validate-${o.id}`}><Check size={14} /></button>
                          <button onClick={() => { setEditObj(o); setEditVal(o.cible || ""); }} title={t("diag_modify")} className="inline-flex items-center justify-center min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 p-1.5 rounded-lg hover:bg-sky-500/15 text-sky-300" data-testid={`obj-modify-${o.id}`}><Pencil size={13} /></button>
                          <button onClick={() => updateObjective(o, "refuse")} title={t("diag_refuse")} className="inline-flex items-center justify-center min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 p-1.5 rounded-lg hover:bg-red-500/15 text-red-300" data-testid={`obj-refuse-${o.id}`}><XIcon size={14} /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <Tabs defaultValue="30" className="w-full">
                <TabsList className="grid grid-cols-3 mb-3">
                  <TabsTrigger value="30" data-testid="plan-tab-30" className="min-h-[44px] sm:min-h-0">30 {lang === "en" ? "days" : lang === "es" ? "días" : "jours"}</TabsTrigger>
                  <TabsTrigger value="60" data-testid="plan-tab-60" className="min-h-[44px] sm:min-h-0">60 {lang === "en" ? "days" : lang === "es" ? "días" : "jours"}</TabsTrigger>
                  <TabsTrigger value="90" data-testid="plan-tab-90" className="min-h-[44px] sm:min-h-0">90 {lang === "en" ? "days" : lang === "es" ? "días" : "jours"}</TabsTrigger>
                </TabsList>
                {["30", "60", "90"].map((h) => (
                  <TabsContent key={h} value={h} className="space-y-3" data-testid={`plan-horizon-${h}`}>
                    {(plan.horizons[h] || []).length === 0 && <p className="text-sm text-[hsl(var(--muted-foreground))] py-4 text-center">—</p>}
                    {(plan.horizons[h] || []).map((a) => (
                      <div key={a.id} className="lb-card p-4" data-testid={`action-${a.id}`}>
                        <div className="flex items-start justify-between gap-2 mb-1.5">
                          <p className="font-semibold text-sm">{a.title}</p>
                          <button onClick={() => cycleStatus(a)} className={`inline-flex items-center min-h-[44px] sm:min-h-0 text-[10px] px-2 py-1 rounded-md border shrink-0 ${STATUS_STYLE[a.status]}`} data-testid={`action-status-${a.id}`}>{t(`st_${a.status}`)}</button>
                        </div>
                        {a.reason && <p className="text-xs text-[hsl(var(--muted-foreground))] mb-2">{a.reason}</p>}
                        {(a.kpi || a.start_value !== null || a.target) && (
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] mb-2">
                            {a.kpi && <span className="text-neon">{t("diag_kpi")} : {a.kpi}</span>}
                            {a.start_value !== null && a.start_value !== undefined && a.start_value !== "" && <span className="text-[hsl(var(--muted-foreground))]">{t("diag_start")} : <b className="text-white">{fmtNum(a.start_value, "", lang)}</b></span>}
                            {a.target && <span className="text-[hsl(var(--muted-foreground))]">{t("diag_target")} : <b className="text-white">{a.target}</b></span>}
                          </div>
                        )}
                        {MODULE_ROUTE[a.module] && (
                          <Button onClick={() => openInModule(a)} className="rounded-full h-11 sm:h-9 px-4 text-xs bg-gradient-to-r from-primary to-neon text-white lb-glow-btn" data-testid={`action-cta-${a.id}`}>
                            {t(EXEC_CTA_KEY[a.module] || "diag_continue_in")}
                            <ChevronRight size={13} className="ml-1" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </TabsContent>
                ))}
              </Tabs>
            </>
          )}
        </section>

        {plan && (
          <NextStepCta testid="diag-next-step"
            description={t("nextstep_plan_desc")} ctaLabel={t("cta_next_plan")}
            onClick={() => nav("/app/plan")} />
        )}

        {/* Dialog modifier objectif */}
        <Dialog open={!!editObj} onOpenChange={(o) => !o && setEditObj(null)}>
          <DialogContent className="bg-[#121218] border-white/10" data-testid="objective-edit-dialog">
            <DialogHeader><DialogTitle>{t("diag_modify")} — {t("diag_objective")}</DialogTitle></DialogHeader>
            {editObj && (
              <div className="space-y-3">
                <p className="text-sm text-[hsl(var(--muted-foreground))]">{editObj.libelle}</p>
                <input value={editVal} onChange={(e) => setEditVal(e.target.value)} data-testid="objective-edit-input"
                  className="w-full rounded-xl bg-white/[0.03] border border-white/10 px-3 py-2.5 text-sm text-white focus:border-neon/50 outline-none" placeholder={t("diag_target")} />
                <Button onClick={() => { updateObjective(editObj, "modifie", editVal); setEditObj(null); }} data-testid="objective-edit-save"
                  className="w-full rounded-full h-11 bg-gradient-to-r from-primary to-neon text-white font-semibold">{t("save")}</Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
        {sourceChooser}
      </div>
    </Layout>
  );
}
